// Preset alert tones, synthesized on the fly into short WAV data URIs so
// they can be used as a plain <audio> src. All presets (including "Classic")
// go through the same generator so every one gets identical, uniform
// urgency-based repeat/pitch/speed scaling - no separate handling needed for
// a static recorded asset.
const SAMPLE_RATE = 44100;

// Different waveform shapes so presets can differ in timbre, not just pitch:
// sine (soft/pure), triangle (mellow), square (harsh/digital-buzzer),
// sawtooth (bright/buzzy horn). phase is in radians.
const oscillate = (wave, phase) => {
  const cycle = ((phase / (2 * Math.PI)) % 1 + 1) % 1; // 0..1, wrap-safe
  switch (wave) {
    case "square":
      return cycle < 0.5 ? 1 : -1;
    case "sawtooth":
      return 2 * cycle - 1;
    case "triangle":
      return 4 * Math.abs(cycle - 0.5) - 1;
    case "sine":
    default:
      return Math.sin(phase);
  }
};

// Square/sawtooth carry more harmonic energy and read as louder/harsher than
// sine/triangle at the same amplitude, so they're scaled down a bit to keep
// levels comparable across presets.
const WAVE_AMPLITUDE = { sine: 0.4, triangle: 0.4, square: 0.28, sawtooth: 0.3 };

const renderTonePattern = (tones) => {
  const totalSamples = tones.reduce(
    (sum, { duration, gap = 0.08 }) => sum + Math.round((duration + gap) * SAMPLE_RATE),
    0
  );
  const samples = new Int16Array(totalSamples);
  let offset = 0;

  tones.forEach(({ freq, sweepTo, freqs, wave = "sine", duration, gap = 0.08 }) => {
    const toneSamples = Math.round(duration * SAMPLE_RATE);
    const gapSamples = Math.round(gap * SAMPLE_RATE);
    const attackSamples = SAMPLE_RATE * 0.005;
    const releaseSamples = SAMPLE_RATE * 0.02;
    const amplitude = WAVE_AMPLITUDE[wave] || 0.4;

    // A chord (freqs) mixes several fixed oscillators for a richer/bell-like
    // timbre; otherwise it's a single oscillator, optionally sweeping from
    // freq to sweepTo (a siren wail) rather than staying fixed pitch. Phase
    // is accumulated per-sample (not computed as sin(2*pi*f*t)) so a sweep's
    // frequency can change continuously without clicks.
    const components = freqs || [freq];
    const phases = components.map(() => 0);

    for (let i = 0; i < toneSamples; i++) {
      const envelope = Math.min(1, i / attackSamples, (toneSamples - i) / releaseSamples);
      const progress = toneSamples > 1 ? i / (toneSamples - 1) : 0;

      let sample = 0;
      components.forEach((baseFreq, idx) => {
        const currentFreq = sweepTo !== undefined ? baseFreq + (sweepTo - baseFreq) * progress : baseFreq;
        phases[idx] += (2 * Math.PI * currentFreq) / SAMPLE_RATE;
        sample += oscillate(wave, phases[idx]) / components.length;
      });

      samples[offset + i] = Math.round(sample * amplitude * envelope * 32767);
    }
    offset += toneSamples + gapSamples;
  });

  return samples;
};

const encodeWav = (samples, sampleRate = SAMPLE_RATE) => {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++, offset += 2) {
    view.setInt16(offset, samples[i], true);
  }

  return buffer;
};

const bufferToDataUri = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return `data:audio/wav;base64,${btoa(binary)}`;
};

const TONE_PATTERNS = {
  // Mellow descending two-note sine "ding-dong".
  classic: [
    { freq: 1046, duration: 0.15, wave: "sine" },
    { freq: 784, duration: 0.15, wave: "sine" },
  ],
  // Bright ascending three-note sine arpeggio.
  chime: [
    { freq: 784, duration: 0.16, wave: "sine" },
    { freq: 988, duration: 0.16, wave: "sine" },
    { freq: 1318, duration: 0.22, wave: "sine" },
  ],
  // Sustained chord (fundamental + overtones) for a resonant bell timbre.
  bell: [{ freqs: [660, 1320, 1980], duration: 0.6, wave: "sine" }],
  // Harsh digital square-wave triple-beep.
  beep: [
    { freq: 1200, duration: 0.09, gap: 0.05, wave: "square" },
    { freq: 1200, duration: 0.09, gap: 0.05, wave: "square" },
    { freq: 1200, duration: 0.09, wave: "square" },
  ],
  // True wailing pitch sweep (rises then falls), like an emergency siren.
  siren: [
    { freq: 500, sweepTo: 1100, duration: 0.5, wave: "sine" },
    { freq: 1100, sweepTo: 500, duration: 0.5, wave: "sine" },
  ],
  // Rapid harsh square-wave buzzer, four tight pulses.
  alarm: [
    { freq: 900, duration: 0.08, gap: 0.04, wave: "square" },
    { freq: 900, duration: 0.08, gap: 0.04, wave: "square" },
    { freq: 900, duration: 0.08, gap: 0.04, wave: "square" },
    { freq: 900, duration: 0.08, wave: "square" },
  ],
  // Low buzzy sawtooth blast, like a car horn.
  horn: [{ freq: 220, duration: 0.35, wave: "sawtooth" }],
  // Soft high triangle-wave triple ping.
  pulse: [
    { freq: 1500, duration: 0.06, gap: 0.05, wave: "triangle" },
    { freq: 1500, duration: 0.06, gap: 0.05, wave: "triangle" },
    { freq: 1500, duration: 0.06, wave: "triangle" },
  ],
};

// How many times the base tone repeats, how tight the gaps between repeats
// are, and how much the pitch shifts, per urgency level. This is baked
// directly into the generated waveform (rather than left to volume/rate
// alone) because repeat count is the most obviously audible "how urgent is
// this" cue - one soft chime vs. six rapid high-pitched chimes are
// unmistakably different, where a small volume/speed tweak on the same
// single tone is easy to miss.
const URGENCY_TONE_PROFILES = {
  1: { repeats: 1, gapMultiplier: 3.0, pitchMultiplier: 0.8 },
  2: { repeats: 2, gapMultiplier: 1.0, pitchMultiplier: 1.0 },
  3: { repeats: 6, gapMultiplier: 0.15, pitchMultiplier: 1.2 },
};
const DEFAULT_TONE_PROFILE = { repeats: 1, gapMultiplier: 1.0, pitchMultiplier: 1.0 };

const buildUrgencyPattern = (basePattern, profile) => {
  const interRepeatGap = 0.18 * profile.gapMultiplier;
  const pattern = [];

  for (let repeat = 0; repeat < profile.repeats; repeat++) {
    basePattern.forEach((tone, i) => {
      const isLastToneInUnit = i === basePattern.length - 1;
      const isLastRepeat = repeat === profile.repeats - 1;
      const baseGap = (tone.gap ?? 0.08) * profile.gapMultiplier;
      pattern.push({
        ...tone,
        freq: tone.freq !== undefined ? tone.freq * profile.pitchMultiplier : undefined,
        sweepTo: tone.sweepTo !== undefined ? tone.sweepTo * profile.pitchMultiplier : undefined,
        freqs: tone.freqs !== undefined ? tone.freqs.map((f) => f * profile.pitchMultiplier) : undefined,
        gap: isLastToneInUnit && !isLastRepeat ? interRepeatGap : baseGap,
      });
    });
  }

  return pattern;
};

const generatedSoundCache = {};

const getGeneratedSoundSrc = (soundId, urgency) => {
  const profile = URGENCY_TONE_PROFILES[urgency] || DEFAULT_TONE_PROFILE;
  const cacheKey = `${soundId}_${urgency || "default"}`;

  if (!generatedSoundCache[cacheKey]) {
    const pattern = buildUrgencyPattern(TONE_PATTERNS[soundId], profile);
    generatedSoundCache[cacheKey] = bufferToDataUri(encodeWav(renderTonePattern(pattern)));
  }
  return generatedSoundCache[cacheKey];
};

export const ALERT_SOUND_OPTIONS = [
  { id: "classic", label: "Classic" },
  { id: "chime", label: "Chime" },
  { id: "bell", label: "Bell" },
  { id: "beep", label: "Beep" },
  { id: "siren", label: "Siren" },
  { id: "alarm", label: "Alarm" },
  { id: "horn", label: "Horn" },
  { id: "pulse", label: "Pulse" },
];

// urgency is optional - omit it (e.g. for a "preview this sound" button) to
// get the plain, single-repeat version of the tone.
export const getSoundSrc = (soundId, urgency) => {
  const id = soundId && TONE_PATTERNS[soundId] ? soundId : "classic";
  return getGeneratedSoundSrc(id, urgency);
};

// Finds the sound configured for the first enabled rule whose keyword
// appears in the alert's DETAILS text; falls back to the classic sound.
export const matchAlertSound = (details, rules = []) => {
  if (details) {
    const lowerDetails = String(details).toLowerCase();
    const match = rules.find(
      (rule) => rule.ENABLED !== 0 && rule.KEYWORD && lowerDetails.includes(String(rule.KEYWORD).toLowerCase())
    );
    if (match) return match.SOUND_ID;
  }
  return "classic";
};

// Urgency levels: 0 = information, 1 = attention, 2 = escalated, 3 = urgent.
// Level 0 stays silent; 1-3 all play the keyword-matched sound. This layer
// (volume + playback speed) applies on top of the repeat/pitch baked into
// getSoundSrc.
const URGENCY_PLAYBACK_SETTINGS = {
  1: { volume: 0.25, playbackRate: 0.6 },
  2: { volume: 0.6, playbackRate: 1.0 },
  3: { volume: 1.0, playbackRate: 2.0 },
};

export const getUrgencyPlaybackSettings = (urgency) =>
  URGENCY_PLAYBACK_SETTINGS[urgency] || null;

// Picks the highest-urgency alert (excluding level 0, which is silent) from
// a flat list of alerts. Returns null if none qualify.
export const findMostUrgentAlert = (alerts = []) => {
  let mostUrgent = null;
  alerts.forEach((alert) => {
    if (alert.URGENCY > 0 && (!mostUrgent || alert.URGENCY > mostUrgent.URGENCY)) {
      mostUrgent = alert;
    }
  });
  return mostUrgent;
};
